import React from "react";

export type CohortMode = "percent" | "absolute";

export type CohortRow = {
    cohort: string;
    values: (number | null)[];
    counts?: (number | null)[];
};

type Props = {
    cohorts: CohortRow[];
    months?: number;
    mode?: CohortMode;
};

const countFormatter = new Intl.NumberFormat("en-US");

// Color is always driven by the retention percentage so the heatmap intensity
// stays meaningful regardless of which number the cell shows.
const cohortColor = (pct: number | null): string => {
    if (pct === null) return "transparent";
    const t = Math.max(0, Math.min(1, pct / 100));
    const a = [0xdd, 0xf3, 0xff];
    const b = [0x0f, 0x8f, 0xe0];
    const c = a.map((v, i) => Math.round(v + (b[i] - v) * t));
    return `rgb(${c.join(", ")})`;
};

const cohortText = (pct: number | null): string => {
    if (pct === null) return "var(--vb-fg-subtle)";
    return pct > 60 ? "#fff" : "var(--vb-neutral-700)";
};

export const CohortTriangle: React.FC<Props> = ({ cohorts, months = 12, mode = "percent" }) => (
    <table className="cohort-table">
        <thead>
            <tr>
                <th className="c-label">Cohort</th>
                {Array.from({ length: months }, (_, i) => (
                    <th key={i}>M{i}</th>
                ))}
            </tr>
        </thead>
        <tbody>
            {cohorts.map((row) => (
                <tr key={row.cohort}>
                    <td className="c-label">{row.cohort}</td>
                    {Array.from({ length: months }, (_, j) => {
                        const pct = row.values[j] ?? null;
                        const isEmpty = pct === null;
                        const count = row.counts?.[j] ?? null;
                        const display =
                            mode === "absolute" && count !== null
                                ? countFormatter.format(count)
                                : isEmpty
                                  ? ""
                                  : String(pct);
                        const title = isEmpty
                            ? ""
                            : `${row.cohort} · M${j} · ${pct}%${
                                  count !== null ? ` · ${countFormatter.format(count)}` : ""
                              }`;
                        return (
                            <td
                                key={j}
                                className={isEmpty ? "cohort-cell cohort-cell--empty" : "cohort-cell"}
                                style={{ background: cohortColor(pct), color: cohortText(pct) }}
                                title={title}
                            >
                                {isEmpty ? "" : display}
                            </td>
                        );
                    })}
                </tr>
            ))}
        </tbody>
    </table>
);
